import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, Star, MapPin, Clock, CheckCircle, Calendar, Shield, Award, Heart, Sparkles } from 'lucide-react';

const BestDentistNearMe = () => {
  const reasons = [
    { icon: <Star className="w-6 h-6" />, title: '500+ 5-Star Reviews', desc: 'Consistently top-rated by patients' },
    { icon: <Award className="w-6 h-6" />, title: 'Award-Winning Care', desc: 'Best of Fort Worth 2025' },
    { icon: <Clock className="w-6 h-6" />, title: 'Same-Day Appointments', desc: 'We respect your time' },
    { icon: <Shield className="w-6 h-6" />, title: '25+ Years Experience', desc: 'Trusted expertise' },
    { icon: <Heart className="w-6 h-6" />, title: 'Gentle Approach', desc: 'Anxiety-free dentistry' },
    { icon: <Sparkles className="w-6 h-6" />, title: 'Latest Technology', desc: 'Modern, comfortable care' },
  ];

  const services = [
    { name: 'General Dentistry', link: '/services/general-dentistry/' },
    { name: 'Dental Implants', link: '/services/dental-implants/' },
    { name: 'Emergency Dental Care', link: '/emergency-dental-care/' },
    { name: 'Cosmetic Dentistry', link: '/services/cosmetic-dentistry/' },
    { name: 'Invisalign', link: '/services/invisalign/' },
    { name: 'LANAP Laser Treatment', link: '/services/lanap/' },
    { name: 'Teeth Whitening', link: '/services/teeth-whitening/' },
    { name: 'Dentures', link: '/dentures/' },
  ];

  const nearbyAreas = [
    'Fort Worth', 'Benbrook', 'White Settlement', 'Westover Hills',
    'River Oaks', 'Aledo', 'Lake Worth', 'Arlington', 'Burleson',
    'Crowley', 'Keller', 'Southlake'
  ];

  return (
    <>
      <Helmet>
        <title>Best Dentist Near Me | Top-Rated Fort Worth Dental</title>
        <meta name="description" content="Searching for the best dentist near me? Fort Worth Dental offers award-winning care with 500+ 5-star reviews. Conveniently located, same-day appointments. Call (817) 985-3337!" />
        <meta name="keywords" content="best dentist near me, top dentist near me, best rated dentist near me, highly rated dentist near me, best local dentist" />
        <link rel="canonical" href="https://fortworthdental.com/best-dentist-near-me" />
        <meta property="og:title" content="Best Dentist Near Me | Fort Worth Dental" />
        <meta property="og:description" content="Find the best dentist near you. 500+ 5-star reviews, award-winning care." />
        <meta property="og:url" content="https://fortworthdental.com/best-dentist-near-me" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Dentist",
            "name": "Fort Worth Dental",
            "description": "Best dentist near you with 500+ 5-star reviews and award-winning care.",
            "url": "https://fortworthdental.com/best-dentist-near-me",
            "telephone": "+1-817-985-3337",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "4620 Citylake Blvd W",
              "addressLocality": "Fort Worth",
              "addressRegion": "TX",
              "postalCode": "76132"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "500"
            },
            "areaServed": nearbyAreas.map(area => ({ "@type": "City", "name": area + ", TX" }))
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 via-teal-700 to-blue-800 text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center gap-1 mb-6">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-8 h-8 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Find the <span className="text-yellow-400">Best Dentist</span> Near You
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-teal-100">
              500+ 5-Star Reviews | Award-Winning Care | Serving Fort Worth & Surrounding Areas
            </p>
            <div className="inline-flex items-center gap-2 bg-white/20 px-6 py-3 rounded-full mb-8">
              <MapPin className="w-5 h-5" />
              <span>4620 Citylake Blvd W, Fort Worth, TX 76132</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:817-985-3337"
                className="inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold py-4 px-8 rounded-lg text-lg transition"
              >
                <Phone className="w-6 h-6" />
                (817) 985-3337
              </a>
              <Link
                to="/appointments/"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-teal-700 font-bold py-4 px-8 rounded-lg text-lg transition"
              >
                <Calendar className="w-6 h-6" />
                Book Appointment
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why We're The Best Near You */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-800">
            Why We're the <span className="text-teal-600">Best Dentist Near You</span>
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            When you search for "best dentist near me," you deserve a practice that truly delivers excellence.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {reasons.map((item, idx) => (
              <div key={idx} className="flex gap-4 p-6 bg-gray-50 rounded-xl hover:shadow-md transition">
                <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-800">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-800">
            Comprehensive Services from Your Best Local Dentist
          </h2>
          <p className="text-center text-gray-600 mb-12">
            Everything you need, all in one convenient location
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {services.map((service, idx) => (
              <Link
                key={idx}
                to={service.link}
                className="bg-white p-4 rounded-lg text-center hover:shadow-md transition hover:bg-teal-50 group"
              >
                <span className="font-semibold text-gray-800 group-hover:text-teal-600">{service.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-800">
            Proudly Serving These Communities
          </h2>
          <p className="text-center text-gray-600 mb-12">
            No matter where you're searching from, we're your best dentist nearby
          </p>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {nearbyAreas.map((area, idx) => (
              <span
                key={idx}
                className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium"
              >
                {area}
              </span>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/areas-we-serve/" className="text-teal-600 hover:text-teal-700 font-semibold">
              View All 37 Service Areas →
            </Link>
          </div>
        </div>
      </section>

      {/* Patient Promise */}
      <section className="py-16 bg-teal-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-800">
              Our Promise to You
            </h2>
            <div className="space-y-4 text-left">
              {[
                'We\'ll treat you like family, not just a patient number.',
                'We\'ll explain everything before we do it—no surprises.',
                'We\'ll respect your time with minimal wait times.',
                'We\'ll work with your budget and insurance.',
                'We\'ll make your comfort our top priority.',
              ].map((promise, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{promise}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Experience the Best Dental Care?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Stop searching for "best dentist near me"—you've found us! 
            Schedule your appointment today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:817-985-3337"
              className="inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold py-4 px-8 rounded-lg text-lg transition"
            >
              <Phone className="w-6 h-6" />
              Call (817) 985-3337
            </a>
            <Link
              to="/appointments/"
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-teal-700 font-bold py-4 px-8 rounded-lg text-lg transition"
            >
              Book Online Now
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default BestDentistNearMe;
